import { Capacitor } from '@capacitor/core'
import { listNewRecordings, readRecordingBytes } from './nativeRecording'
import { readAndroidCallLog, matchCallToRecording } from './androidCallLog'
import { isAlreadyUploaded, addAuditEntry } from './recordingAuditLog'
import { getLastSyncedAt, advanceLastSyncedAt } from './syncState'
import { getCachedCallLogPermStatus } from './callLogPermission'
import { leadService } from '../../leads/services/leads'
import { normalizeIndianMobile } from '../../../lib/phoneNormalize'
import api from '../../../lib/api'

export interface IncomingMatch {
  lead_id: number
  customer_name: string
  call_log_id: number
}

export interface ScanResult {
  uploaded: number
  skipped: number          // dedup
  unmatched: number        // personal calls
  errors: number
  incomingMatches: IncomingMatch[]   // matched incoming calls from known leads
  callLogAvailable: boolean          // false when READ_CALL_LOG not granted
}

let scanInProgress = false

export type ScanStatusCallback = (status: 'started' | 'done', result?: ScanResult) => void

/**
 * Run an ambient scan: find all new OEM recordings, match them to leads,
 * upload. Personal/unknown calls are skipped silently.
 *
 * Safe to call on every app foreground — no-ops if already running or not native.
 */
export async function runAmbientScan(onStatus?: ScanStatusCallback): Promise<ScanResult | null> {
  if (!Capacitor.isNativePlatform()) return null
  if (scanInProgress) return null

  scanInProgress = true
  onStatus?.('started')

  const callLogAvailable = getCachedCallLogPermStatus() === 'granted'
  const result: ScanResult = { uploaded: 0, skipped: 0, unmatched: 0, errors: 0, incomingMatches: [], callLogAvailable }

  try {
    const since = getLastSyncedAt()
    const [recordings, callLogEntries] = await Promise.all([
      listNewRecordings(since),
      callLogAvailable ? readAndroidCallLog(since) : Promise.resolve([]),
    ])

    for (const rec of recordings) {
      try {
        // Step 1: read bytes + compute MD5
        const data = await readRecordingBytes(rec.path)
        if (!data) { result.errors++; continue }
        const { bytes, md5, ext } = data

        // Step 2: dedup check
        if (isAlreadyUploaded(md5)) { result.skipped++; continue }

        // Step 3: identify which call this belongs to
        const match = await resolveCallLog(rec, callLogEntries)
        if (!match) { result.unmatched++; continue }

        // Step 4: upload
        const blob = new Blob([bytes.buffer as ArrayBuffer], { type: `audio/${ext}` })
        const form = new FormData()
        form.append('file', blob, `ambient_${match.callLogId}.${ext}`)
        form.append('md5', md5)

        const res = await api.post(`/telephony/calls/${match.callLogId}/recording`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        if (res.data.status === 'uploaded' || res.data.status === 'duplicate') {
          addAuditEntry({
            filename: rec.name,
            md5,
            uploaded_at: new Date().toISOString(),
            call_log_id: match.callLogId,
          })
          result.uploaded++
          if (match.isIncoming && match.lead) {
            result.incomingMatches.push({
              lead_id: match.lead.lead_id,
              customer_name: match.lead.customer_name,
              call_log_id: match.callLogId,
            })
          }
        } else {
          result.errors++
        }
      } catch {
        result.errors++
      }
    }

    advanceLastSyncedAt()
  } catch {
    result.errors++
  } finally {
    scanInProgress = false
    onStatus?.('done', result)
  }

  return result
}

interface ResolvedCall {
  callLogId: number
  isIncoming: boolean
  lead?: { lead_id: number; customer_name: string; status: string }
}

/**
 * Determine which CallLog stub to attach a recording to.
 * Returns null when the call is unmatched (personal call) — skip silently.
 */
async function resolveCallLog(
  rec: { path: string; mtime: number },
  callLogEntries: Awaited<ReturnType<typeof readAndroidCallLog>>,
): Promise<ResolvedCall | null> {
  const deviceCall = matchCallToRecording(callLogEntries, rec.mtime)
  if (!deviceCall) return null   // READ_CALL_LOG unavailable — skip

  const normalized = normalizeIndianMobile(deviceCall.number)
  if (normalized.length !== 10) return null

  const lead = await leadService.getLeadByMobileHash(normalized)
  if (!lead) return null  // personal / no active lead

  // Create retrospective stub
  const stub = await leadService.mobileInitiateCall(lead.lead_id, {
    direction: deviceCall.direction,
    started_at: deviceCall.started_at.toISOString(),
    duration_s: deviceCall.duration_s,
  })

  return {
    callLogId: stub.call_log_id,
    isIncoming: deviceCall.direction === 'inbound',
    lead,
  }
}
